// Shared by server and client components (no server-only imports here).
export const skeletons = ['clinical', 'studio'] as const
export type Skeleton = (typeof skeletons)[number]

// Staff-only design preview (?skeleton= or the preview panel): session cookie, never saved in the global.
export const PREVIEW_SKELETON_COOKIE = 'ercf_preview_skeleton'
export const CONSENT_COOKIE = 'ercf_consent'
// Studio side menu collapsed/expanded (functional preference, no consent needed).
export const SIDEBAR_COOKIE = 'ercf_sidebar'
// CNIL recommends asking again after 6 months.
export const CONSENT_MAX_AGE = 60 * 60 * 24 * 182

export const asSkeleton = (value?: string | null): Skeleton | null => (skeletons as readonly string[]).includes(value ?? '') ? (value as Skeleton) : null

export type Consent = { decided: boolean; analytics: boolean; social: boolean }

// Cookie value: "necessary" or a comma list of granted optional categories ("analytics,social").
export function parseConsent(value?: string | null): Consent {
  if (!value) return { decided: false, analytics: false, social: false }
  const granted = new Set(decodeURIComponent(value).split(','))
  return { decided: true, analytics: granted.has('analytics'), social: granted.has('social') }
}

export function serializeConsent(consent: Omit<Consent, 'decided'>): string {
  const granted = [consent.analytics && 'analytics', consent.social && 'social'].filter(Boolean)
  return granted.length ? granted.join(',') : 'necessary'
}

export const CONSENT_EVENT = 'ercf:consent'
export const CONSENT_OPEN_EVENT = 'ercf:consent-open'

// Client side: write the consent cookie (path covers the base path) and notify listeners (GA, Facebook block).
export function storeConsent(consent: Omit<Consent, 'decided'>) {
  const base = process.env.NEXT_PUBLIC_BASE_PATH || '/'
  document.cookie = `${CONSENT_COOKIE}=${serializeConsent(consent)}; path=${base}; max-age=${CONSENT_MAX_AGE}; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { ...consent, decided: true } }))
}

export function readConsentCookie(): Consent {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=([^;]*)`))
  return parseConsent(match?.[1])
}
